import { useEffect, useState } from 'react';
import { MessagesSquare, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useIsCompactSidebar } from 'hooks/use-mobile';
import { cn } from 'lib/utils';

type MobilePreviewToggleProps = {
  onClick: () => void;
  className?: string;
};

export function MobilePreviewToggle({ onClick, className }: MobilePreviewToggleProps) {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      data-testid="mobile-preview-toggle"
      className={cn(
        'inline-flex size-10 shrink-0 items-center justify-center rounded-full border border-[#D9DBE9] bg-white text-[#170F49] shadow-sm lg:hidden dark:border-border dark:bg-card dark:text-foreground',
        className,
      )}
      aria-label={t('PREVIEW', { defaultValue: 'Preview' })}
      onClick={onClick}
    >
      <MessagesSquare className="size-5" aria-hidden />
    </button>
  );
}

type MobilePreviewPanelProps = {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
};

export function MobilePreviewPanel({
  open,
  onClose,
  children,
  className,
}: MobilePreviewPanelProps) {
  const { t } = useTranslation();
  const isCompact = useIsCompactSidebar();
  const [offsetTop, setOffsetTop] = useState(0);

  useEffect(() => {
    if (!isCompact) return undefined;

    const update = () => {
      const header = document.querySelector<HTMLElement>('[data-testid="app-header"]');
      setOffsetTop(header ? Math.round(header.getBoundingClientRect().bottom) : 0);
    };

    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [isCompact, open]);

  useEffect(() => {
    if (!open || !isCompact) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isCompact, onClose, open]);

  return (
    <aside
      data-testid="mobile-preview-panel"
      aria-hidden={isCompact && !open ? true : undefined}
      style={isCompact ? { top: offsetTop } : undefined}
      className={cn(
        'flex min-h-0 w-full flex-col overflow-hidden',
        'max-lg:fixed max-lg:inset-x-0 max-lg:bottom-0 max-lg:z-50 max-lg:bg-background',
        'max-lg:transition-transform max-lg:duration-300 max-lg:ease-out',
        open
          ? 'max-lg:translate-x-0'
          : 'max-lg:pointer-events-none max-lg:translate-x-full',
        'lg:relative lg:inset-auto lg:top-auto lg:z-auto lg:h-auto lg:w-[340px] lg:flex-none lg:translate-x-0 xl:w-[360px]',
        className,
      )}
    >
      {children}
      <button
        type="button"
        data-testid="mobile-preview-close"
        className="absolute right-4 top-4 z-20 inline-flex size-10 items-center justify-center rounded-full border border-[#D9DBE9] bg-white text-[#170F49] shadow-sm lg:hidden dark:border-border dark:bg-card dark:text-foreground"
        aria-label={t('CLOSE', { defaultValue: 'Close' })}
        onClick={onClose}
      >
        <X className="size-5" aria-hidden />
      </button>
    </aside>
  );
}
